import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/layout/Navbar";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Eye } from "lucide-react";

const MODE_COPY = {
  verify: { label: "Verified", tone: "bg-acid", note: "Every event is signature-checked." },
  reject: { label: "Fail-closed", tone: "bg-white", note: "Live key with no signing secret — all events rejected." },
  accept_unverified: { label: "Unverified", tone: "bg-white", note: "Test mode — events accepted without a signature." },
};

export default function AdminSettings() {
  const [status, setStatus] = useState(null);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/settings/stripe-webhook-secret").then((r) => setStatus(r.data));

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/admin/settings/stripe-webhook-secret", { value: value.trim() });
      toast.success(`Saved. Webhook mode: ${r.data.verification_mode}`);
      setValue("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save that value");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    try {
      await api.delete("/admin/settings/stripe-webhook-secret");
      toast.success("Cleared. Falling back to the environment variable.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear");
    }
  };

  const mode = MODE_COPY[status?.verification_mode] || MODE_COPY.reject;

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <section className="border-b-2 border-ink">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 py-16">
          <div className="overline mb-3">// Admin · settings</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter">
            Payment security
          </h1>
          <p className="text-muted-foreground mt-4 max-w-xl text-sm">
            Values entered here are encrypted before storage and can never be read back — not through
            this page, any endpoint, or the logs.
          </p>
        </div>
      </section>

      <section className="max-w-[900px] mx-auto px-6 md:px-12 py-12">
        <div className="border-2 border-ink bg-white" data-testid="webhook-secret-card">
          <div className="border-b-2 border-ink px-6 py-4 flex items-center justify-between">
            <div>
              <div className="overline">// Stripe webhook signing secret</div>
              <p className="font-display font-black text-2xl uppercase tracking-tight mt-1">
                {status?.configured ? "Configured" : "Not configured"}
              </p>
            </div>
            <div className={`border-2 border-ink px-3 py-2 ${mode.tone}`} data-testid="verification-mode-badge">
              <div className="flex items-center gap-2 font-mono uppercase text-xs tracking-widest font-bold">
                {status?.verification_mode === "verify"
                  ? <ShieldCheck className="w-4 h-4" />
                  : <ShieldAlert className="w-4 h-4" />}
                {mode.label}
              </div>
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <p className="text-sm font-mono text-muted-foreground">{mode.note}</p>

            {status && (
              <dl className="grid grid-cols-2 gap-4 text-sm border-2 border-ink p-4" data-testid="secret-source-table">
                <dt className="overline">Resolved from</dt>
                <dd className="font-mono" data-testid="resolved-from">{status.resolved_from || "nothing"}</dd>
                <dt className="overline">Env var present</dt>
                <dd className="font-mono">{String(status.env?.raw_present)}{status.env?.is_placeholder ? " (placeholder)" : ""}</dd>
                <dt className="overline">Admin-entered</dt>
                <dd className="font-mono">
                  {status.admin_db?.configured
                    ? `${status.admin_db.shape?.prefix}… · ${status.admin_db.shape?.length} chars`
                    : "none"}
                </dd>
                <dt className="overline">Last updated</dt>
                <dd className="font-mono">{status.admin_db?.updated_at?.slice(0, 19) || "—"}</dd>
              </dl>
            )}

            <form onSubmit={save} className="space-y-4">
              <label className="block">
                <span className="overline">// Paste signing secret</span>
                <input
                  type="password"
                  autoComplete="off"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="whsec_…"
                  className="mt-2 w-full border-2 border-ink bg-bone px-4 py-3 font-mono text-sm focus:outline-none focus:bg-white"
                  data-testid="webhook-secret-input"
                />
              </label>
              <p className="text-xs font-mono text-muted-foreground flex items-start gap-2">
                <Eye className="w-4 h-4 shrink-0" />
                {status?.hint}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={saving || value.trim().length < 20}
                  className="brut-btn-primary disabled:opacity-40"
                  data-testid="save-webhook-secret"
                >
                  {saving ? "Saving…" : "Save secret"}
                </button>
                {status?.admin_db?.configured && (
                  <button type="button" onClick={clear} className="brut-btn" data-testid="clear-webhook-secret">
                    Clear stored secret
                  </button>
                )}
              </div>
            </form>

            <p className="text-xs font-mono text-muted-foreground border-t-2 border-ink pt-4">
              Resolution order: a real value in the environment wins, this admin-entered value is the
              fallback, and a <span className="font-bold">whsec_000…</span> scaffold always counts as absent.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
